import { useState, useEffect } from 'react';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { adminApi } from '../lib/api';

// Convert base64 URL to Uint8Array for VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function PushNotificationToggle({ className = '' }) {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [permission, setPermission] = useState('default');

  useEffect(() => {
    checkSupport();
  }, []);

  const checkSupport = async () => {
    // Check if push notifications are supported
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (!supported) {
      setIsLoading(false);
      return;
    }

    // Check current permission
    const perm = Notification.permission;
    setPermission(perm);

    // Check if already subscribed
    try {
      const response = await adminApi.getPushStatus();
      setIsSubscribed(response.data.subscribed);
    } catch (error) {
      console.error('Error checking push status:', error);
    }

    setIsLoading(false);
  };

  const subscribe = async () => {
    setIsLoading(true);
    
    try {
      // Request permission
      const permission = await Notification.requestPermission();
      setPermission(permission);
      
      if (permission !== 'granted') {
        toast.error('Permission refusée pour les notifications');
        setIsLoading(false);
        return;
      }

      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      // Get VAPID public key
      const vapidResponse = await adminApi.getVapidKey();
      const vapidKey = vapidResponse.data.public_key;
      
      if (!vapidKey) {
        toast.error('Clé VAPID non configurée');
        setIsLoading(false);
        return;
      }

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });

      // Send subscription to server
      const subscriptionJson = subscription.toJSON();
      await adminApi.subscribeToPush({
        endpoint: subscriptionJson.endpoint,
        keys: subscriptionJson.keys
      });

      setIsSubscribed(true);
      toast.success('Notifications activées !');
    } catch (error) {
      console.error('Error subscribing to push:', error);
      toast.error('Erreur lors de l\'activation des notifications');
    }

    setIsLoading(false);
  };

  const unsubscribe = async () => {
    setIsLoading(true);
    
    try {
      // Get current subscription
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
      }

      // Remove from server
      await adminApi.unsubscribeFromPush();

      setIsSubscribed(false);
      toast.success('Notifications désactivées');
    } catch (error) {
      console.error('Error unsubscribing from push:', error);
      toast.error('Erreur lors de la désactivation');
    }

    setIsLoading(false);
  };

  if (!isSupported) {
    return null; // Don't show if not supported
  }

  if (permission === 'denied') {
    return (
      <div className={`flex items-center gap-2 text-sm text-muted-foreground ${className}`}>
        <BellOff className="w-4 h-4" />
        <span>Notifications bloquées</span>
      </div>
    );
  }

  return (
    <Button
      variant={isSubscribed ? 'default' : 'outline'}
      size="sm"
      onClick={isSubscribed ? unsubscribe : subscribe}
      disabled={isLoading}
      className={className}
    >
      {isLoading ? (
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
      ) : isSubscribed ? (
        <Bell className="w-4 h-4 mr-2" />
      ) : (
        <BellOff className="w-4 h-4 mr-2" />
      )}
      {isSubscribed ? 'Notifications actives' : 'Activer les notifications'}
    </Button>
  );
}

export default PushNotificationToggle;
